import React, { useEffect, useState } from "react";
import DatePicker from "../generic/DatePicker";
import Select from "../generic/Select";
import MultiSelector from "../generic/MultiSelector";
import LocationPicker from "../generic/LocationPicker";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "components/ui/label";
import AddSkills from "components/crewManagement/AddSkills";
import AddEquipments from "components/generic/AddEquipments";
import TimePicker from "components/crewManagement/TimePicker";
import { getEventTypes } from "lib/api/requirementsApi";
import { calculateDuration } from "lib/utils";

const convertEventTypes = (res) => {
  return res.map((item) => ({
    value: String(item.event_type_id),
    label: item.event_type_name,
  }));
};

const RequirementForm = ({ passRef, handleSubmit, initialData }) => {
  const options = [
    { value: "1", label: "IVS" },
    { value: "2", label: "YouTube" },
    { value: "3", label: "Facebook" },
    { value: "4", label: "Twitch" },
    { value: "5", label: "LinkedIn" },
    { value: "6", label: "Twitter" },
  ];
  const crewOption = [
    { value: "1", label: "Director" },
    { value: "2", label: "Camera Operator" },
    { value: "3", label: "Audio Engineer" },
    { value: "4", label: "Lighting Technician" },
    { value: "5", label: "Video Editor" },
    { value: "6", label: "Stream Engineer" },
    { value: "7", label: "Production Manager" },
    { value: "8", label: "Graphics Designer" },
  ];
  const skillOptions = [
    { value: "13", label: "Director" },
    { value: "12", label: "Livestream Audio" },
    { value: "2", label: "Video Event" },
    { value: "3", label: "Video Music" },
    { value: "4", label: "Video Lifestyle" },
    { value: "5", label: "Photo Portrait" },
    { value: "6", label: "Photo Product" },
    { value: "7", label: "Photo Event" },
    { value: "8", label: "Photo Lifestyle" },
    { value: "9", label: "Audio Engineer" },
    { value: "10", label: "Creative Director" },
    { value: "11", label: "Livestream Director" },
    { value: "1", label: "Video Commercial" },
  ];
  const qualityOptions = [
    { value: "720", label: "720 HD" },
    { value: "1080", label: "1080 Full HD" },
    { value: "4k", label: "4K Ultra HD" },
  ];
  const crewSizeOptions = [
    { value: "3", label: "Small (2-3 people)" },
    { value: "5", label: "Medium (4-6 people)" },
    { value: "7", label: "Large (7+ people)" },
  ];

  const [eventTypes, setEventTypes] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [selectedQuality, setSelectedQuality] = useState("");
  const [selectedCrewSize, setSelectedCrewSize] = useState("");
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedSP, setSelectedSP] = useState([]);
  const [selectedCrew, setSelectedCrew] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [projectName, setProjectName] = useState("");
  const [description, setDescription] = useState("");
  const [duration, setDuration] = useState({ hours: 0, formatted: "00h 00m" });
  const [budget, setBudget] = useState("");
  const [expectedViewers, setExpectedViewers] = useState("");
  const [streamingDestinations, setStreamingDestinations] = useState({});

  const [errors, setErrors] = useState({
    projectName: false,
    selectedSP: false,
    selectedCrew: false,
    selectedSkills: false,
    selectedEquipment: false,
    times: false,
    streamQuality: false,
  });

  const isLiveStreaming = React.useMemo(() => {
    const currentEvent = eventTypes.find((e) => e.value === selectedEvent);
    return currentEvent?.label?.toLowerCase().includes("stream") || false;
  }, [eventTypes, selectedEvent]);

  // LOGIC TO CHECK IF END TIME IS NEXT DAY
  const isNextDay = React.useMemo(() => {
    if (!startTime || !endTime) return false;
    const toMinutes = (timeStr) => {
      const [h, m] = timeStr.split(":").map(Number);
      return h * 60 + m;
    };
    return toMinutes(endTime) < toMinutes(startTime);
  }, [startTime, endTime]);

  // EFFECT TO POPULATE FORM IN EDIT MODE
  useEffect(() => {
    if (initialData) {
      setProjectName(initialData.project_name || "");
      setDescription(initialData.description || "");
      setSelectedEvent(String(initialData.event_type || ""));
      setSelectedDate(initialData.event_date || "");
      setStartTime(initialData.start_time || "");
      setEndTime(initialData.end_time || "");
      setBudget(initialData.budget || "");
      setExpectedViewers(initialData.expected_viewers || "");
      setSelectedQuality(initialData.stream_quality || "");
      setSelectedCrewSize(String(initialData.crew_size_needed || ""));

      // Handle the Array/JSON fields
      const parseField = (field) => {
        if (Array.isArray(field)) return field;
        try { return JSON.parse(field || "[]"); } catch (e) { return []; }
      };

      const sp = parseField(initialData.streaming_platforms);
      setSelectedSP(sp);
      setSelectedCrew(parseField(initialData.crew_roles));
      setSelectedSkills(parseField(initialData.skills_needed || initialData.required_skills));
      setSelectedEquipment(parseField(initialData.equipments_needed));

      // Populate RTMP destinations if they exist in initialData
      if (initialData.destinations && Array.isArray(initialData.destinations)) {
        const destObj = {};
        initialData.destinations.forEach(d => {
          destObj[d.platform] = d.rtmp_url;
        });
        setStreamingDestinations(destObj);
      }

      if (initialData.event_location) {
        try {
          setSelectedLocation(typeof initialData.event_location === 'string'
            ? JSON.parse(initialData.event_location)
            : initialData.event_location);
        } catch (e) { setSelectedLocation(null); }
      }
    }
  }, [initialData]);

  useEffect(() => {
    getEventTypes()
      .then((res) => setEventTypes(convertEventTypes(res?.data)))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (startTime && endTime) {
      setDuration(calculateDuration(startTime, endTime));
    }
  }, [startTime, endTime]);

  const onSubmit = (e) => {
    e.preventDefault();

    const newErrors = {
      projectName: !projectName.trim(),
      projectName: !projectName.trim(),
      selectedSP: isLiveStreaming ? selectedSP.length === 0 : false,
      selectedCrew: selectedCrew.length === 0,
      selectedSkills: selectedSkills.length === 0,
      selectedEquipment: selectedEquipment.length === 0,
      times: !startTime || !endTime,
      streamQuality: isLiveStreaming ? !selectedQuality : false,
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some((hasError) => hasError)) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const formData = {
      project_name: projectName,
      description,
      event_type: selectedEvent,
      event_date: selectedDate,
      duration_hours: duration?.hours || 0,
      budget,
      expected_viewers: expectedViewers,
      budget,
      expected_viewers: expectedViewers,
      stream_quality: isLiveStreaming ? selectedQuality : "",
      crew_size_needed: selectedCrewSize,
      event_location: selectedLocation ? JSON.stringify(selectedLocation) : "",
      streaming_platforms: isLiveStreaming ? selectedSP : [],
      streaming_destinations: isLiveStreaming
        ? Object.keys(streamingDestinations)
          .map((platformId) => ({
            platform: platformId,
            rtmp_url: streamingDestinations[platformId],
          }))
          .filter((dest) => selectedSP.includes(dest.platform))
        : [],
      crew_roles: selectedCrew,
      required_skills: selectedSkills,
      equipment_categories_needed: selectedEquipment,
      start_time: startTime,
      end_time: endTime,
    };

    console.log("Submitting Payload:", formData);
    handleSubmit(formData);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.target.tagName === "INPUT") e.preventDefault();
  };

  return (
    <form
      ref={passRef}
      onSubmit={onSubmit}
      onKeyDown={handleKeyDown}
      className="flex flex-col gap-6"
    >
      {/* Section - Project Details */}
      <div className="border border-border bg-card rounded-2xl items-start p-6">
        <div className="flex flex-col gap-1.5 mb-6">
          <div className="text-foreground text-2xl font-semibold leading-6">
            Project Details
          </div>
          <div className="text-muted-foreground text-sm font-normal leading-5">
            Basic information about your project
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 w-full">
            <Label
              htmlFor="project_name"
              className={`label text-sm font-bold ${errors.projectName ? "text-red-500" : ""
                }`}
            >
              Project Name <span className="text-red-500">*</span>
            </Label>
            <Input
              type="text"
              id="project_name"
              placeholder="e.g., Annual Tech Conference 2024"
              value={projectName}
              onChange={(e) => {
                setProjectName(e.target.value);
                if (e.target.value.trim())
                  setErrors((prev) => ({ ...prev, projectName: false }));
              }}
              error={errors.projectName}
            />
          </div>
          <div className="flex flex-col gap-2 w-full">
            <Label
              htmlFor="project_description"
              className="label text-sm font-bold"
            >
              Description
            </Label>
            <div className="flex flex-col gap-2 w-full">
              <Textarea
                name="project_description"
                id="project_description"
                placeholder="Describe your event, objectives, and any special requirements..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="flex flex-col lg:flex-row gap-4 w-full mt-4">
              <div className="w-full flex flex-col gap-2">
                <Label htmlFor="event_type" className="label text-sm font-bold">
                  Event Type
                </Label>
                <Select
                  id="event_type"
                  options={eventTypes}
                  placeholder="Select Type"
                  value={selectedEvent}
                  onChange={setSelectedEvent}
                />
              </div>

              <div className="w-full flex flex-col gap-2">
                <Label htmlFor="event_date" className="label text-sm font-bold">
                  Event Date
                </Label>
                <DatePicker
                  id="event_date"
                  value={selectedDate}
                  onChange={(dateStr) => setSelectedDate(dateStr)}
                />
              </div>
            </div>

            <div className="flex gap-4 w-full mt-4">
              <div className="w-full flex flex-col gap-2">
                <Label
                  htmlFor="event_location"
                  className="label text-sm font-bold"
                >
                  Event Location
                </Label>
                <LocationPicker
                  value={selectedLocation}
                  onChange={setSelectedLocation}
                  placeholder="Click to select venue location on map"
                />
              </div>
            </div>

            <div className="flex gap-4 w-full mt-4">
              <div className="w-full flex flex-col gap-2">
                <Label
                  htmlFor="expected_viewers"
                  className="label text-sm font-bold"
                >
                  Expected Viewers
                </Label>
                <Input
                  type="text"
                  name="expected_viewers"
                  id="expected_viewers"
                  placeholder="Enter expected viewers"
                  value={expectedViewers}
                  onChange={(e) => setExpectedViewers(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Time & Budget */}
      <div
        className={`border rounded-2xl p-6 ${errors.times ? "border-red-500 bg-red-50/10" : "border-border bg-card"
          }`}
      >
        <div className="text-foreground text-2xl font-bold mb-4">
          Time & Budget{" "}
          {errors.times && (
            <span className="text-sm font-normal text-red-500">
              (Please set times)
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="start_time" className="label text-sm font-bold">
              Start Time <span className="text-red-500">*</span>
            </Label>
            <TimePicker id="start_time" setTime={setStartTime} value={startTime} />
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="end_time" className="label text-sm font-bold">
                End Time <span className="text-red-500">*</span>
              </Label>
              {isNextDay && (
                <span className="text-[10px] font-bold bg-orange-100 text-orange-600 px-2 py-0.5 rounded uppercase border border-orange-200">
                  Next Day
                </span>
              )}
            </div>
            <TimePicker id="end_time" setTime={setEndTime} value={endTime} />
          </div>

          <div className="flex flex-col gap-2">
            <Label
              htmlFor="project_duration"
              className="label text-sm font-bold"
            >
              Duration (hours)
            </Label>
            <div className="relative">
              <p className="rounded-lg border-[1.5px] border-input bg-background text-sm py-2 px-3 flex justify-between items-center">
                <span>{duration.formatted}</span>
                {isNextDay && (
                  <span className="text-xs text-muted-foreground italic">
                    Overnight event
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="project_budget" className="label text-sm font-bold">
              Budget ($)
            </Label>
            <Input
              type="number"
              placeholder="Enter budget"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Skills Required */}
      <div
        className={`border rounded-2xl p-6 ${errors.selectedSkills
          ? "border-red-500 bg-red-50/10"
          : "border-border bg-card"
          }`}
      >
        <div className="text-2xl font-bold mb-4">
          Skills Required <span className="text-red-500">*</span>
        </div>
        <AddSkills
          value={selectedSkills}
          onChange={(val) => {
            setSelectedSkills(val);
            if (val.length > 0)
              setErrors((prev) => ({ ...prev, selectedSkills: false }));
          }}
          options={skillOptions}
          error={errors.selectedSkills}
        />
        {errors.selectedSkills && (
          <p className="text-red-500 text-xs mt-2">
            Please add at least one skill
          </p>
        )}
      </div>

      {/* Technical Requirements */}
      <div className="border border-border bg-card rounded-2xl p-6">
        <div className="flex flex-col gap-1.5 mb-6">
          <div className="text-foreground text-2xl font-semibold">
            Technical Requirements
          </div>
          <div className="text-muted-foreground text-sm leading-5">
            Specify your technical and crew needs
          </div>
        </div>

        <div className="flex flex-col gap-6">
          {isLiveStreaming && (
            <div className="flex flex-col gap-2 w-full">
              <Label
                htmlFor="stream_platforms"
                className={`label text-sm font-bold ${errors.selectedSP ? "text-red-500" : ""
                  }`}
              >
                Streaming Platforms <span className="text-red-500">*</span>
              </Label>
              <div
                className={
                  errors.selectedSP ? "rounded-lg ring-1 ring-red-500" : ""
                }
              >
                <MultiSelector
                  id="stream_platforms"
                  options={options}
                  selectedValue={selectedSP}
                  setSelectedValue={(val) => {
                    setSelectedSP(val);
                    if (val.length > 0)
                      setErrors((prev) => ({ ...prev, selectedSP: false }));

            //       setStreamingDestinations((prev) => {
            //         const next = { ...prev };
            //         if (Array.isArray(val)) {
            //           val.forEach((platformId) => {
            //             if (next[platformId] === undefined) {
            //               let prefix = "";
            //               if (platformId === "2") prefix = "rtmp://a.rtmp.youtube.com/live2";
            //               if (platformId === "3") prefix = "rtmps://live-api-s.facebook.com:443/rtmp/";
            //               if (platformId === "4") prefix = "rtmp://live.twitch.tv/app/";
            //               next[platformId] = prefix;
            //             }
            //           });
            //         }
            //         return next;
            //       });
            //     }}
            //   />
            // </div>

            // {selectedSP.length > 0 && (
            //   <div className="mt-4 flex flex-col gap-4 p-4 bg-accent/30 rounded-xl border border-border/50">
            //     <p className="text-sm font-semibold">Streaming Platform URLs</p>
            //     {selectedSP.map((platformId) => {
            //       const platform = options.find((o) => o.value === platformId);
            //       return (
            //         <div key={platformId} className="flex flex-col gap-2">
            //           <Label
            //             htmlFor={`url-${platformId}`}
            //             className="text-xs font-medium text-muted-foreground uppercase"
            //           >
            //             {platform?.label} RTMP URL
            //           </Label>
            //           <Input
            //             id={`url-${platformId}`}
            //             type="text"
            //             placeholder="rtmp://... or rtmps://..."
            //             value={streamingDestinations[platformId] || ""}
            //             onChange={(e) => {
            //               setStreamingDestinations((prev) => ({
            //                 ...prev,
            //                 [platformId]: e.target.value,
            //               }));
            //             }}
            //           />
            //         </div>
            //       );
            //     })}
                    setStreamingDestinations((prev) => {
                      const next = { ...prev };
                      if (Array.isArray(val)) {
                        val.forEach((platformId) => {
                          if (next[platformId] === undefined) {
                            let prefix = "";
                            if (platformId === "2")
                              prefix = "rtmp://a.rtmp.youtube.com/live2/";
                            if (platformId === "3")
                              prefix = "rtmps://live-api-s.facebook.com:443/rtmp/";
                            if (platformId === "4")
                              prefix = "rtmp://live.twitch.tv/app/";
                            next[platformId] = prefix;
                          }
                        });
                      }
                      return next;
                    });
                  }}
                />
              </div>

              {selectedSP.length > 0 && (
                <div className="mt-4 flex flex-col gap-4 p-4 bg-accent/30 rounded-xl border border-border/50">
                  <p className="text-sm font-semibold">
                    Streaming Platform URLs
                  </p>
                  {selectedSP.map((platformId) => {
                    const platform = options.find(
                      (o) => o.value === platformId
                    );
                    return (
                      <div key={platformId} className="flex flex-col gap-2">
                        <Label
                          htmlFor={`url-${platformId}`}
                          className="text-xs font-medium text-muted-foreground uppercase"
                        >
                          {platform?.label} RTMP URL
                        </Label>
                        <Input
                          id={`url-${platformId}`}
                          type="text"
                          placeholder="rtmp://... or rtmps://..."
                          value={streamingDestinations[platformId] || ""}
                          onChange={(e) => {
                            setStreamingDestinations((prev) => ({
                              ...prev,
                              [platformId]: e.target.value,
                            }));
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col md:flex-row gap-4 w-full">
            {isLiveStreaming && (
              <div className="w-full flex flex-col gap-2">
                <Label
                  htmlFor="stream_quality"
                  className={`label text-sm font-bold ${errors.streamQuality ? "text-red-500" : ""
                    }`}
                >
                  Stream Quality <span className="text-red-500">*</span>
                </Label>
                <div
                  className={
                    errors.streamQuality
                      ? "rounded-lg ring-1 ring-red-500"
                      : ""
                  }
                >
                  <Select
                    id="stream_quality"
                    options={qualityOptions}
                    placeholder="Select quality"
                    value={selectedQuality}
                    onChange={(val) => {
                      setSelectedQuality(val);
                      if (val)
                        setErrors((prev) => ({ ...prev, streamQuality: false }));
                    }}
                  />
                </div>
              </div>
            )}

            <div className="w-full flex flex-col gap-2">
              <Label htmlFor="crew_size" className="label text-sm font-bold">
                Crew Size Needed
              </Label>
              <Select
                id="crew_size"
                options={crewSizeOptions}
                placeholder="Select crew size"
                value={selectedCrewSize}
                onChange={setSelectedCrewSize}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label
              htmlFor="crew_roles_required"
              className={`label text-sm font-bold ${errors.selectedCrew ? "text-red-500" : ""
                }`}
            >
              Crew Roles Required <span className="text-red-500">*</span>
            </Label>
            <div
              className={
                errors.selectedCrew ? "rounded-lg ring-1 ring-red-500" : ""
              }
            >
              <MultiSelector
                id="crew_roles_required"
                options={crewOption}
                selectedValue={selectedCrew}
                setSelectedValue={(val) => {
                  setSelectedCrew(val);
                  if (val.length > 0)
                    setErrors((prev) => ({ ...prev, selectedCrew: false }));
                }}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label
              htmlFor="equipment_needed"
              className={`label text-sm font-bold ${errors.selectedEquipment ? "text-red-500" : ""
                }`}
            >
              Equipment Needed <span className="text-red-500">*</span>
            </Label>
            <div
              className={
                errors.selectedEquipment
                  ? "border border-red-500 rounded-lg p-1"
                  : ""
              }
            >
              <AddEquipments
                id="equipment_needed"
                value={selectedEquipment}
                onChange={(val) => {
                  setSelectedEquipment(val);
                  if (val.length > 0)
                    setErrors((prev) => ({
                      ...prev,
                      selectedEquipment: false,
                    }));
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default RequirementForm;
